import { Box, Text, VStack } from "@chakra-ui/react";
import { Field, FormInput, FormTextarea, FormSelect } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader, Grid2 } from "@/components/jobOffer/ui/layout";
import { AddButton, ItemHeader, RepeatableText } from "@/components/jobOffer/ui/listHelpers";

const GOAL_TYPE_OPTIONS = [
    { value: "Purchase", label: "Zakup produktu" },
    { value: "Registration", label: "Rejestracja na usługę" },
    { value: "Meeting", label: "Umówienie spotkania" },
    { value: "Form", label: "Formularz" },
    { value: "HandoffToHuman", label: "Przekazanie do handlowca" },
];

const CALENDAR_SYSTEM_OPTIONS = [
    { value: "CalCom", label: "Cal.com" },
    { value: "Microsoft365", label: "Microsoft 365" },
    { value: "GoogleCalendar", label: "Google Calendar" },
    { value: "Other", label: "Inny system (API)" },
];

const newGoal = () => ({ goalType: "Meeting", link: "", description: "", preferredCta: [] });

const StepConversion = ({ values, onChange }) => {
    const set = (key, val) => onChange({ ...values, [key]: val });

    const goals = values.conversionGoals ?? [];
    const setGoals = (next) => set("conversionGoals", next);
    const updateGoal = (i, patch) => setGoals(goals.map((g, idx) => idx === i ? { ...g, ...patch } : g));
    const removeGoal = (i) => setGoals(goals.filter((_, idx) => idx !== i));
    const addGoal = () => setGoals([...goals, newGoal()]);

    const escalation = values.escalationConfig ?? {};
    const setEscalation = (patch) => set("escalationConfig", { ...escalation, ...patch });

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={7} title="Cele kampanii i CTA" subtitle="Do czego Agent ma dążyć w rozmowie" />
                <VStack align="stretch" gap={4}>
                    {goals.map((g, i) => (
                        <Box key={i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
                            <ItemHeader title={GOAL_TYPE_OPTIONS.find(o => o.value === g.goalType)?.label ?? "Cel"} index={i} onRemove={() => removeGoal(i)} />
                            <VStack align="stretch" gap={3}>
                                <Field label="Typ celu" required>
                                    <FormSelect value={g.goalType} onChange={(v) => updateGoal(i, { goalType: v })} options={GOAL_TYPE_OPTIONS} placeholder="Wybierz typ" />
                                </Field>
                                <Field label="Link" hint="np. link do zakupu/rejestracji/kalendarza">
                                    <FormInput value={g.link ?? ""} onChange={(e) => updateGoal(i, { link: e.target.value })} />
                                </Field>
                                <Field label="Opis"><FormTextarea value={g.description ?? ""} onChange={(e) => updateGoal(i, { description: e.target.value })} rows={2} /></Field>
                                {g.goalType === "Meeting" && (
                                    <Grid2>
                                        <Field label="System kalendarza">
                                            <FormSelect value={g.calendarSystem ?? ""} onChange={(v) => updateGoal(i, { calendarSystem: v || null })} options={CALENDAR_SYSTEM_OPTIONS} placeholder="Wybierz system" />
                                        </Field>
                                        <Field label="Link do kalendarza">
                                            <FormInput value={g.calendarLink ?? ""} onChange={(e) => updateGoal(i, { calendarLink: e.target.value })} placeholder="np. https://cal.com/..." />
                                        </Field>
                                    </Grid2>
                                )}
                                <RepeatableText
                                    label="Preferowane CTA dla tego celu"
                                    values={g.preferredCta ?? []}
                                    onChange={(v) => updateGoal(i, { preferredCta: v })}
                                    placeholder="np. Umów prezentację"
                                />
                            </VStack>
                        </Box>
                    ))}
                    <AddButton label="Dodaj cel konwersji" onClick={addGoal} />
                    <Box borderWidth={1} borderColor="border.primary" borderRadius={8} p={3} bg="bg.highlight">
                        <Text fontSize="xs" color="text.grey">
                            Agent nie pokazuje CTA mechanicznie po każdej odpowiedzi — prezentuje je dopiero, gdy rozpozna moment gotowości zakupowej klienta.
                        </Text>
                    </Box>
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={8} title="Eskalacja i przekazanie do człowieka" subtitle="Kiedy i jak Agent ma przekazać rozmowę handlowcowi" />
                <VStack align="stretch" gap={4}>
                    <RepeatableText
                        label="Warunki eskalacji"
                        values={escalation.conditions ?? []}
                        onChange={(v) => setEscalation({ conditions: v })}
                        placeholder='np. "klient gotowy podpisać umowę"'
                    />
                    <Field label="Próg wartości transakcji do eskalacji (PLN)" hint="powyżej tej kwoty Agent przekazuje rozmowę handlowcowi">
                        <FormInput
                            type="number"
                            min="0"
                            value={escalation.transactionValueThreshold ?? ""}
                            onChange={(e) => setEscalation({ transactionValueThreshold: e.target.value === "" ? null : Number(e.target.value) })}
                            placeholder="np. 20000"
                        />
                    </Field>
                    <Field label="Szablon podsumowania przy przekazaniu" hint="dostępne placeholdery: {imie}, {nazwisko}, {firma}, {potrzeba}, {budzet}, {termin}">
                        <FormTextarea
                            value={escalation.handoffSummaryTemplate ?? ""}
                            onChange={(e) => setEscalation({ handoffSummaryTemplate: e.target.value })}
                            rows={4}
                        />
                    </Field>
                </VStack>
            </SectionCard>
        </VStack>
    );
};

export default StepConversion;
